import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CustomersService } from '../../../_services/customers/customers.service';
import { BaseComponent } from '../../../components/base/base.component';
import { RolesService } from '../../../_services/roles/roles.service';
import { SessionService } from '../../../_services/session/session.service';
import IRole from '../../../_interfaces/IRole';
import ISite from '../../../_interfaces/ISite';
import IUser, { IUserHasRole } from '../../../_interfaces/IUser';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

@Component({
  standalone: true,
  selector: 'app-form-user-access',
  templateUrl: './form-user-access.component.html',
  styleUrls: ['./form-user-access.component.scss'],
  imports: [
    TableModule,
    SelectModule,
    FormsModule,
    TranslatePipe,
    InputTextModule,
    ButtonModule,
  ],
})
export class FormUserAccessComponent extends BaseComponent implements OnInit {
  @Input('affectedUser')
  public affectedUser: IUser;
  public sites: ISite[];
  public roles: IRole[];
  public userHasRoles: IUserHasRole[];
  public sitesRoles: any = {};
  public siteFilter = '';
  public bulkRoleId = -1;

  constructor(
    public messagesService: MessageService,
    public errorsService: ErrorsService,
    public translate: TranslateService,
    public customersService: CustomersService,
    public session: SessionService,
    public rolesService: RolesService
  ) {
    super(session);
  }

  loadRoles() {
    return this.rolesService.getByCustomer(this.customers_id).pipe(
      tap((roles: IRole[]) => {
        this.roles = roles;
        this.roles.unshift({
          id: -1,
          name: this.translate.instant('COMMON.NO_ACCESS'),
        });
      }),
      catchError((err: HttpErrorResponse) => {
        this.messagesService.add({
          severity: 'error',
          detail: this.translate.instant('MESSAGES.COMMON.500.DETAIL'),
          summary: this.translate.instant('MESSAGES.COMMON.500.SUMMARY'),
        });
        return throwError(() => new Error(err.error));
      })
    );
  }

  loadUserRoles() {
    if (!!!this.affectedUser.id) {
      return new Observable();
    }
    return this.customersService
      .getRoles(this.customers_id, this.affectedUser.id)
      .pipe(
        tap((customers: any[]) => {
          this.sites.forEach((site) => {
            if (site.id) this.sitesRoles[site.id] = -1;
          });
          customers
            .find((customer) => {
              return customer.id == this.customers_id;
            })
            .sites.forEach((site: ISite) => {
              if (site.id && site.role) this.sitesRoles[site.id] = site.role.id;
            });
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => new Error(err.error));
        })
      );
  }

  loadSites() {
    return this.customersService.getSites(this.customers_id).pipe(
      tap((sites: ISite[]) => {
        this.sites = sites;
      }),
      catchError((err: HttpErrorResponse) => {
        this.messagesService.add({
          severity: 'error',
          detail: this.errorsService.getMessageFromSiteError(err),
          summary: 'Error',
        });
        return throwError(() => new Error(err.error));
      })
    );
  }

  updateRole(site: ISite) {
    if (!!!site.id) return;
    const roles_id = this.sitesRoles[site.id];
    const role = this.roles.find((_) => _.id === roles_id) || { name: '' };
    const sites_id = site.id;
    this.customersService
      .updateUserRoleOnSite(
        this.customers_id,
        this.affectedUser,
        sites_id,
        roles_id
      )
      .subscribe(
        () => {
          this.messagesService.add({
            severity: 'success',
            summary: this.translate.instant(
              'MESSAGES.USERS.PERMISSIONS.SUMMARY'
            ),
            detail: this.translate.instant(
              'MESSAGES.USERS.PERMISSIONS.DETAIL',
              {
                role: role.name,
                user: this.affectedUser.email,
                site: site.name,
              }
            ),
          });
        },
        () => {
          this.messagesService.add({
            severity: 'error',
            summary: this.translate.instant('MESSAGES.COMMON.500.SUMMARY'),
            detail: this.translate.instant('MESSAGES.COMMON.500.DETAIL', {
              role: role.name,
              user: this.affectedUser.email,
              site: site.name,
            }),
          });
        }
      );
  }

  get filteredSites(): ISite[] {
    const filter = (this.siteFilter || '').trim().toLowerCase();
    if (!filter) return this.sites || [];
    return (this.sites || []).filter((site) =>
      (site.name || '').toLowerCase().includes(filter),
    );
  }

  applyBulkRoleOnFilteredSites() {
    if (!this.affectedUser?.id) return;

    const siteIds = this.filteredSites
      .map((site) => site.id)
      .filter((id): id is number => !!id);

    if (!siteIds.length) return;

    this.customersService
      .bulkUpdateUserRoleOnSites(
        this.customers_id,
        this.affectedUser,
        siteIds,
        +this.bulkRoleId,
      )
      .subscribe({
        next: () => {
          siteIds.forEach((siteId) => {
            this.sitesRoles[siteId] = +this.bulkRoleId;
          });

          const roleName =
            this.roles.find((role) => role.id === +this.bulkRoleId)?.name || '';

          this.messagesService.add({
            severity: 'success',
            summary: this.translate.instant('MESSAGES.USERS.PERMISSIONS.SUMMARY'),
            detail: this.translate.instant('MESSAGES.USERS.PERMISSIONS.BULK.DETAIL', {
              role: roleName,
              user: this.affectedUser.email,
              count: siteIds.length,
            }),
          });
        },
        error: () => {
          this.messagesService.add({
            severity: 'error',
            summary: this.translate.instant('MESSAGES.COMMON.500.SUMMARY'),
            detail: this.translate.instant('MESSAGES.COMMON.500.DETAIL'),
          });
        },
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
    forkJoin([this.loadSites(), this.loadRoles()]).subscribe(() => {
      this.bulkRoleId = -1;
      this.loadUserRoles().subscribe();
    });
  }
}
